import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { updateMeeting } from '../../controllers/meetingsController';
import {
  apiStringToDatetimeLocalInput,
  datetimeLocalToApiString,
} from '../../utils/turkeyLocalDateTime';
import MeetingMealPriceField, {
  useWeeklyMeetingMealConfig,
} from './components/MeetingMealPriceField';

const EditMeetingDialog = ({ open, onClose, meeting, onUpdated }) => {
  const [form, setForm] = useState({
    title: '',
    meetingDate: '',
    meetingEndDate: '',
    meetingRoomName: '',
    location: '',
    onlineMeetingUrl: '',
    description: '',
    allowGuests: false,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const {
    loading: mealConfigLoading,
    error: mealConfigError,
    mealPrice: configMealPrice,
    currency: configCurrency,
  } = useWeeklyMeetingMealConfig(open);

  useEffect(() => {
    if (open && meeting) {
      setForm({
        title: meeting.title || '',
        meetingDate: apiStringToDatetimeLocalInput(meeting.meetingDate),
        meetingEndDate: apiStringToDatetimeLocalInput(meeting.meetingEndDate),
        meetingRoomName: meeting.meetingRoomName || '',
        location: meeting.location || '',
        onlineMeetingUrl: meeting.onlineMeetingUrl || '',
        description: meeting.description || '',
        allowGuests: meeting.allowGuests || false,
        isActive: meeting.isActive !== undefined ? meeting.isActive : true,
      });
    }
  }, [open, meeting]);

  const handleUpdate = async () => {
    if (!form.title || !form.meetingDate) {
      toast.error('Başlık ve toplantı tarihi zorunludur');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: meeting.id,
        title: form.title,
        meetingDate: datetimeLocalToApiString(form.meetingDate),
        meetingEndDate: datetimeLocalToApiString(form.meetingEndDate),
        meetingRoomName: form.meetingRoomName?.trim() || null,
        location: form.location || null,
        onlineMeetingUrl: form.onlineMeetingUrl || null,
        description: form.description || null,
        mealPrice: configMealPrice,
        currency: configCurrency,
        allowGuests: form.allowGuests,
        isActive: form.isActive,
      };

      const updated = await updateMeeting(payload);
      toast.success('Toplantı başarıyla güncellendi');
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || 'Toplantı güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
    },
    '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
  };

  const multilineTextFieldSx = {
    ...textFieldSx,
    '& .MuiInputBase-input': { fontSize: '14px', px: 0, py: 0 },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Toplantı Düzenle
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Başlık
            </Typography>
            <TextField
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              required
              placeholder=""
              inputProps={{ maxLength: 200 }}
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Toplantı Tarihi
            </Typography>
            <TextField
              type="datetime-local"
              value={form.meetingDate}
              onChange={(e) => setForm({ ...form, meetingDate: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Toplantı Bitiş Tarihi
            </Typography>
            <TextField
              type="datetime-local"
              value={form.meetingEndDate}
              onChange={(e) => setForm({ ...form, meetingEndDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Toplantı Salonu
            </Typography>
            <TextField
              value={form.meetingRoomName}
              onChange={(e) => setForm({ ...form, meetingRoomName: e.target.value })}
              fullWidth
              placeholder="Salon adı (opsiyonel)"
              inputProps={{ maxLength: 200 }}
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Lokasyon
            </Typography>
            <TextField
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              fullWidth
              placeholder=""
              inputProps={{ maxLength: 500 }}
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Online Toplantı URL
            </Typography>
            <TextField
              value={form.onlineMeetingUrl}
              onChange={(e) => setForm({ ...form, onlineMeetingUrl: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              placeholder="https://..."
              sx={textFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Açıklama
            </Typography>
            <TextField
              multiline
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              placeholder=""
              inputProps={{ maxLength: 2000 }}
              sx={multilineTextFieldSx}
            />
          </Box>
          <Grid container spacing={2}>
            <MeetingMealPriceField
              loading={mealConfigLoading}
              error={mealConfigError}
              mealPrice={configMealPrice}
              currency={configCurrency}
              textFieldSx={textFieldSx}
            />
          </Grid>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Misafir davet edilebilir mi?
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.allowGuests}
                  onChange={(e) => setForm({ ...form, allowGuests: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                  }}
                />
              }
              label={form.allowGuests ? 'Evet' : 'Hayır'}
              sx={{ m: 0 }}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Durum
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                  }}
                />
              }
              label={form.isActive ? 'Aktif' : 'Pasif'}
              sx={{ m: 0 }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', color: '#666666' }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={saving || !form.title || !form.meetingDate}
          sx={{
            textTransform: 'none',
            borderRadius: '5px',
            backgroundColor: '#0E4D94',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMeetingDialog;
